import { Router, RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/store/modules/user'
import { usePermissionsStore } from '@/store/modules/user/permissions'
import NProgress from '@/config/nprogress'
import { isStaticMode } from '@/routers/mode'
import { getAccessToken, clearAuthTokens } from '@/auth/jwt'

function isUrl(url: string): boolean {
  return /^https?:\/\//.test(url) || url.startsWith('//')
}

const BI_PAGE = '/bi'
const BIVIEW_PAGE = '/biview'
const DYN_MARK = '__DYNAMIC_ROUTES_LOADED__'

// 进入应用（已登录）即向 online 接口获取用户信息，所有模式/环境均生效
// （不再用持久化缓存或一次性标志跳过，确保每次进入守卫都调用 user/online）


function toLogin(to: any) {
  const query = Object.assign({}, to.query, { redirect: to.path })
  if (to.path === '/login') {
    return '/login'
  }
  return { path: '/login', query }
}

/**
 * 路由守卫
 * @param router 路由实例
 * @param routerMode 路由模式：static（读取子模块 routes.ts）| dynamic（后台权限路由）
 */
const guard = (router: Router, routerMode: 'static' | 'dynamic' = 'dynamic') => {
  const WhiteListPage = [BI_PAGE, BIVIEW_PAGE]

  // 确保 404 兜底路由存在
  const ensureNotFound = () => {
    if (!router.hasRoute('not-found')) {
      router.addRoute({
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        meta: { title: '404', hidden: true },
        component: () => import('@/components/404/index.vue')
      })
    }
  }
  ensureNotFound()

  router.beforeEach(async (to, from, next) => {
    document.title = (to.meta.title as string) || '驰田美容管理系统'
    NProgress.start()
    const userStore = useUserStore()
    const permissionStore = usePermissionsStore()

    // 外链：新标签页打开并中止当前 SPA 导航
    if (isUrl(to.fullPath)) {
      window.open(to.fullPath, '_blank')
      NProgress.done()
      next(false)
      return
    }

    // 获取 token（axios-jwt 返回 Promise，必须 await）
    const accessToken = await getAccessToken()

    // 白名单页面（BI 驾驶舱等）
    if (WhiteListPage.includes(to.fullPath) || to.path === '/login') {
      // 已登录访问登录页 -> 回首页
      if (to.path === '/login' && accessToken) {
        next('/')
        NProgress.done()
        return
      }
      if (to.path === '/login' || WhiteListPage.includes(to.fullPath)) {
        resetCatch()
        next()
        NProgress.done()
        return
      }
    }

    // 未登录 -> 登录页
    if (!accessToken) {
      NProgress.done()
      next(toLogin(to))
      return
    }

    // ===== 静态模式：路由全部来自 routes.ts，不加载后台权限 =====
    if (routerMode === 'static' || isStaticMode()) {
      // 静态模式不加载权限路由，但仍需获取用户信息（user/online）
      // 仅刚登录或缓存异常（顶层 id 为 0，如旧缓存未写入 id）时调用 online，其余情况读取 store 缓存
      if (!userStore.id) {
        try {
          await userStore.getUserInfo()
        } catch {
          // 获取用户信息失败（如 token 过期），清除 token 并跳转登录页
          await clearAuthTokens()
          next(toLogin(to))
          NProgress.done()
          return
        }
      }
      // 同步 keep-alive 缓存
      const cache: string[] = []
      router.getRoutes().forEach((r) => {
        if (r.meta?.keepalive && r.name) {
          cache.push(r.name as string)
        }
      })
      permissionStore.setCacheRouter(cache)
      next()
      NProgress.done()
      return
    }

    // ===== 动态模式：根据后台权限生成路由 =====
    // 以路由实例中的标记路由为准（内存态，刷新后必然重新注入），
    // 不能依赖 store 里的 isDynamicRouteLoaded：一旦被持久化污染会跳过注入导致全局 404
    const hasDyn = router.hasRoute(DYN_MARK)
    if (!hasDyn) {
      // 动态模式每次进入应用（登录/刷新）都先拉取最新用户信息（含权限菜单），
      // 保证菜单与路由始终与 online 接口返回的 permissions 一致，不读取旧缓存
      try {
        await userStore.getUserInfo()
      } catch {
        // getUserInfo 失败（如 token 过期），清除 token 并跳转登录页
        await clearAuthTokens()
        next(toLogin(to))
        NProgress.done()
        return
      }
      const perms = userStore.getPermissions ?? []
      if (perms.length === 0) {
        // 权限为空，无法生成动态路由
        next()
        NProgress.done()
        return
      }
      // 同步最新权限到 PermissionsStore（持久化），避免 initRoutesOnRefresh 用旧缓存渲染旧菜单
      permissionStore.permissions = perms
      await permissionStore.getAsyncMenusFrom(perms, true)
      // 解析 componentKey -> component（LAYOUT / PARENT_VIEW / 视图组件），否则 addRoute 无 component
      permissionStore.restoreComponents(permissionStore.asyncMenus)
      // 动态菜单整体作为 'Layout' 路由的子路由挂载
      permissionStore.asyncMenus.forEach((route) => {
        router.addRoute('Layout', route as RouteRecordRaw)
      })
      router.addRoute({
        path: '/__dyn_route_marker__',
        name: DYN_MARK,
        meta: { hidden: true },
        component: { render: () => null }
      })
      ensureNotFound()
      resetCatch()
      permissionStore.setDynamicRouteLoaded(true)
      // 重新触发当前导航：只能按 path 跳转，不能展开 to（首次匹配命中的是兜底 404 路由，
      // 展开后携带 name: 'not-found'，vue-router 优先按 name 解析会再次落到 404 页）
      next({ path: to.path, query: to.query, hash: to.hash, replace: true })
      NProgress.done()
      return
    }
    next()
  })

  router.afterEach(async (to) => {
    // 登录页 / 白名单页不执行后续逻辑
    if (to.path === '/login' || WhiteListPage.includes(to.fullPath)) {
      NProgress.done()
      return
    }

    if (routerMode === 'static' || isStaticMode()) {
      NProgress.done()
      return
    }

    const accessToken = await getAccessToken()
    if (!accessToken) {
      // 已无有效 token（如刚退出登录），不再调用 user/online，避免 401 报错
      NProgress.done()
      return
    }

    // 与 beforeEach 保持一致：以路由实例中的标记路由判断是否已注入动态路由
    const dynamicRouteLoaded = router.hasRoute(DYN_MARK)
    if (dynamicRouteLoaded) {
      if (!useUserStore().id) {
        await useUserStore().getUserInfo()
      }
      NProgress.done()
      return
    }
    const perms = useUserStore().getPermissions ?? []
    if (perms.length === 0) {
      useUserStore().logout()
      NProgress.done()
      return
    }
    // 同步最新权限到 PermissionsStore（持久化），避免 initRoutesOnRefresh 用旧缓存渲染旧菜单
    usePermissionsStore().permissions = perms
    await usePermissionsStore().getAsyncMenusFrom(perms, true)
    usePermissionsStore().restoreComponents(usePermissionsStore().asyncMenus)
    usePermissionsStore().asyncMenus.forEach((route) => {
      router.addRoute('Layout', route as RouteRecordRaw)
    })
    usePermissionsStore().setDynamicRouteLoaded(true)
    ensureNotFound()
    resetCatch()
    NProgress.done()
  })
}

function resetCatch() {}

export default guard
